<template>
  <div class="mt-3 container">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h1>{{ $t("myProjects") }}</h1>

      <div class="d-flex align-items-center">
        <!-- Add New Project Button -->
        <button class="btn btn-primary" @click="addNewProject">
          {{ $t("addNewProject") }}
          <i class="fas fa-plus ms-2"></i>
        </button>

        <!-- Search Box -->
        <div class="search-container">
          <input
            type="text"
            class="form-control search-input"
            :placeholder="$t('searchPlaceholder')"
            v-model="searchQuery"
          />
        </div>
      </div>
    </div>

    <!-- Project List -->
    <ProjectSummaryComponent
      v-for="project in filteredProjects"
      :key="project.id"
      :project="project"
      @projectSelected="openProject"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import ProjectSummaryComponent from "@/components/ProjectSummaryComponent.vue";
import { useProjectStore } from "@/stores/project";
import { useProjectSummaryStore } from "@/stores/project";
import { useMapStateStore } from "@/stores/mapState";
import { storeToRefs } from "pinia";
import type { ProjectSummary } from "@/types/projectTypes";
import { useI18n } from "vue-i18n";

export default defineComponent({
  name: "ProjectsListView",
  components: { ProjectSummaryComponent },
  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const projectStore = useProjectStore();
    const mapStateStore = useMapStateStore();
    const projectSummaryStore = useProjectSummaryStore();
    const { projectSummaries } = storeToRefs(projectSummaryStore);
    const searchQuery = ref("");

    const filteredProjects = computed(() => {
      let filteredList = projectSummaries.value;
      if (searchQuery.value) {
        filteredList = filteredList.filter((p: ProjectSummary) =>
          p.project_name.toLowerCase().includes(searchQuery.value.toLowerCase())
        );
      }
      return filteredList;
    });

    const addNewProject = async () => {
      await mapStateStore.clearMapState();
      await projectStore.clearProject();
      router.push("/project");
    };

    const openProject = async (projectId: number) => {
      try {
        await projectStore.clearProject();
        await projectStore.fetchProject(projectId);
        router.push(`/project/${projectId}`);
      } catch (error) {
        console.error("Failed to open project:", error);
        // Handle error (show message to user)
        alert(t("projects.errors.failedToOpen"));
      }
    };

    // Load project summaries when component mounts
    onMounted(async () => {
      try {
        await projectSummaryStore.fetchProjectSummaries();
      } catch (error) {
        console.error("Failed to load project summaries:", error);
        alert(t("projects.errors.failedToLoad"));
      }
    });

    return {
      searchQuery,
      filteredProjects,
      addNewProject,
      openProject,
    };
  },
});
</script>

<style scoped>
.container {
  padding-top: 80px;
  min-height: 100vh;
  position: relative;
  z-index: 1;
}

h1 {
  color: var(--color-dark);
  padding-left: 20px;
  margin: 0;
}

.search-container {
  flex: 1;
  max-width: 500px;
  padding-left: 20px;
}

.search-input {
  width: 100%;
  height: 40px;
  font-size: 16px;
  background-color: var(--color-white);
  border: 1px solid var(--color-light);
  border-radius: 2px;
  padding: 0 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.search-input:focus {
  border-color: var(--secondary-color);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  outline: none;
}

.search-input::placeholder {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23A6A6A6' viewBox='0 0 16 16'%3E%3Cpath d='M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z'/%3E%3C/svg%3E ");
  background-repeat: no-repeat;
  background-position: 0 center;
  background-size: 16px 16px;
  padding-left: 20px;
}



.btn-primary {
  background-color: var(--secondary-color);
  border: none;
  color: var(--color-white);
  transition: all 0.3s ease;
  border-radius: 2px;
}

.btn-primary:hover {
  background-color: var(--primary-color);
  filter: brightness(0.9);
  transform: translateY(-1px);
}

/* Add hover effect for project summary components */
:deep(.card) {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

:deep(.card:hover) {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}
</style>
